import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();

  // Redirect if not authenticated or not admin
  useEffect(() => {
    if (!token) {
      console.log('No token - redirecting to login');
      navigate('/login');
      return;
    }
    if (!user) {
      console.log('No user - redirecting to login');
      navigate('/login');
      return;
    }
    if (user.role !== 'admin') {
      console.log('User is not admin:', user);
      alert('Access denied. Admin privileges required.');
      navigate('/');
      return;
    }
  }, [user, token, navigate]);

  // Show loading or nothing while checking auth
  if (!user || !token || user.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  const navigation = [
    { name: 'Home', href: '/admin', icon: '🏠', mobileIcon: '🏠' },
    { name: 'Products', href: '/admin/products', icon: '📦', mobileIcon: '📦' },
    { name: 'Orders', href: '/admin/orders', icon: '🛒', mobileIcon: '🛒' },
    { name: 'Users', href: '/admin/users', icon: '👥', mobileIcon: '👥' },
    { name: 'Menu', href: '/admin/cache', icon: '⚡', mobileIcon: '☰' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar - Hidden on mobile */}
      <nav className="hidden md:block bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/admin" className="flex items-center">
                <div className="w-8 h-8 bg-indigo-600 rounded flex items-center justify-center">
                  <span className="text-white font-bold text-lg">H</span>
                </div>
                <span className="ml-2 text-xl font-bold text-gray-900">Admin Panel</span>
              </Link>
            </div>

            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="text-sm text-gray-700 hover:text-gray-900"
              >
                View Store
              </Link>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">{user?.full_name}</span>
                <button
                  onClick={handleLogout}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Top Bar */}
      <nav className="md:hidden bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-40">
        <div className="px-4">
          <div className="flex justify-between items-center h-14">
            <Link to="/admin" className="flex items-center">
              <div className="w-7 h-7 bg-indigo-600 rounded flex items-center justify-center">
                <span className="text-white font-bold">H</span>
              </div>
              <span className="ml-2 text-lg font-bold text-gray-900">House of PM</span>
            </Link>
            <Link to="/" className="text-indigo-600 text-sm font-medium">
              Store
            </Link>
          </div>
        </div>
      </nav>

      <div className="flex md:pt-16">
        {/* Desktop Sidebar Navigation */}
        <div className="hidden md:block w-64 bg-white shadow-sm fixed left-0 top-16 bottom-0 overflow-y-auto">
          <nav className="mt-5 px-2">
            <div className="space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-indigo-100 text-indigo-900'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <span className="mr-3 text-xl">{item.icon}</span>
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 md:ml-64 pt-14 md:pt-0 pb-16 md:pb-0">
          {children}
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
        <div className="flex justify-around items-center h-16">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className="flex flex-col items-center justify-center flex-1 h-full space-y-1"
              >
                <span className={`text-2xl ${
                  isActive ? 'text-indigo-600' : 'text-gray-400'
                }`}>
                  {item.mobileIcon}
                </span>
                <span className={`text-xs font-medium ${
                  isActive ? 'text-indigo-600' : 'text-gray-500'
                }`}>
                  {item.name}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default AdminLayout;
